// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSpinner,
  faTrash,
  faEdit,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
// eslint-disable-next-line no-unused-vars
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import SecureLS from "secure-ls";
import TopActionMenus from "./TopActionMenus";
import { useNavigationEvent } from "navigation-react";
import EditCustomer from "./EditCustomer";

const ls = new SecureLS({ encodingType: "aes" });

// Function to get CSRF token
const username = "admin1";
const password = "abc@123@Q";
const authHeader = "Basic " + btoa(`${username}:${password}`);

// Function to get CSRF token
const getCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        credentials: "include",
        headers: {
          Authorization: authHeader,
          Accept: "text/plain",
        },
      },
    );

    if (response.ok) {
      const token = await response.text();
      ls.set("usercsrf_token", token);
      return token;
    }

    const storedToken = ls.get("usercsrf_token");
    if (storedToken) {
      return storedToken;
    }

    return null;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    return ls.get("usercsrf_token") || null;
  }
};

const fetcher = async (url) => {
  try {
    const csrfToken = await getCsrfToken();

    const headers = {
      Authorization: authHeader,
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      credentials: "include",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

export default function CustomerRegister() {
  // eslint-disable-next-line no-unused-vars
  const { stateNavigator } = useNavigationEvent();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // 'all', 'received', 'issue'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Edit Customer States
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Fetch customer
  const { data: customers, mutate: mutateCustomers } = useSWR(
    `${process.env.REACT_APP_CUSTOMER_API}?sort=-changed&fields[node--customer]=title,field_customer_email,field_phone_number,field_customer_address,drupal_internal__nid,created,changed`,
    fetcher,
  );
  console.log("Customers Register Data:", customers);

  // Fetch products
  const { data: productList, mutate: mutateProducts } = useSWR(
    `${process.env.REACT_APP_ADD_PRODUCT_API}?fields[node--product]=title,field_sku,field_stock_quantity,field_price&page[limit]=100`,
    fetcher,
  );

  // Create lookup objects
  const productLookup =
    productList?.data?.reduce((acc, product) => {
      acc[product.id] = {
        title: product.attributes.title,
        sku: product.attributes.field_sku,
        current_stock: parseInt(product.attributes.field_stock_quantity) || 0,
        selling_price: parseFloat(product.attributes.field_price) || 0,
      };
      return acc;
    }, {}) || {};

  // Calculate Statistics
  const calculateStatistics = () => {
    if (!customers?.data) {
      return {
        totalCustomers: 0,
        newThisMonth: 0,
        newThisWeek: 0,
        activeCustomers: 0,
        customersWithEmail: 0,
        customersWithPhone: 0,
      };
    }

    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
    startOfWeek.setHours(0, 0, 0, 0);

    const totalCustomers = customers.data.length;

    // Count customers created this month
    const newThisMonth = customers.data.filter((customer) => {
      const createdDate = new Date(customer.attributes.created);
      return createdDate >= startOfMonth;
    }).length;

    // Count customers created this week
    const newThisWeek = customers.data.filter((customer) => {
      const createdDate = new Date(customer.attributes.created);
      return createdDate >= startOfWeek;
    }).length;

    // Count active customers (created in last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const activeCustomers = customers.data.filter((customer) => {
      const createdDate = new Date(customer.attributes.created);
      return createdDate >= thirtyDaysAgo;
    }).length;

    // Count customers with email
    const customersWithEmail = customers.data.filter(
      (customer) =>
        customer.attributes.field_customer_email &&
        customer.attributes.field_customer_email.trim() !== "",
    ).length;

    // Count customers with phone
    const customersWithPhone = customers.data.filter(
      (customer) =>
        customer.attributes.field_phone_number &&
        customer.attributes.field_phone_number.trim() !== "",
    ).length;

    return {
      totalCustomers,
      newThisMonth,
      newThisWeek,
      activeCustomers,
      customersWithEmail,
      customersWithPhone,
    };
  };

  const stats = calculateStatistics();

  // Handle Edit Customer
  const handleEditClick = (customer) => {
    setEditingCustomer(customer);
    setIsEditModalOpen(true);
  };

  const handleEditSave = (updatedCustomer) => {
    console.log("Customer updated successfully:", updatedCustomer);
    // Optional: Add success notification here
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditingCustomer(null);
  };

  // Function to delete customer
  const deleteCustomer = async (customerId, customerData) => {
    if (!customerId) {
      console.error("Invalid customer ID");
      return;
    }

    try {
      const csrfToken = await getCsrfToken();
      if (!csrfToken) {
        alert("Authentication failed");
        return;
      }

      // Show confirmation dialog
      if (
        window.confirm(
          "Are you sure you want to delete this Customer? This action cannot be undone.",
        )
      ) {
        // Just send DELETE request - Drupal module will handle stock reversal
        const response = await fetch(
          `${process.env.REACT_APP_CUSTOMER_API}/${customerId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: authHeader,
              "Content-Type": "application/vnd.api+json",
              Accept: "application/vnd.api+json",
              "X-CSRF-Token": csrfToken,
            },
            credentials: "include",
          },
        );

        if (response.ok) {
          // Show success message
          alert("Customer deleted successfully.");

          // Refresh data
          mutateCustomers();
          mutateProducts();
        } else if (response.status === 403) {
          // Handle deletion prevented by Drupal
          const errorData = await response.json();
          alert(
            `Cannot delete: ${
              errorData.message || "Product has been sold in sales orders."
            }`,
          );
        } else {
          alert(
            `This invoice has a sales attached. Cannot be deleted. Error: ${response.status}`,
          );
        }
      }
    } catch (error) {
      console.error("Error deleting stock movement:", error);
      alert("An error occurred while deleting the stock movement.");
    }
  };

  // Filter movements
  const filteredMovements = () => {
    if (!customers?.data) return [];

    let filtered = customers.data.filter((movement) => {
      // Filter by type
      if (
        filterType !== "all" &&
        movement.attributes.field_movement_type !== filterType
      ) {
        return false;
      }

      // Filter by search
      if (searchTerm) {
        const customerName = movement.attributes.title || "";
        const email = movement.attributes.field_customer_email || "";
        const phone = movement.attributes.field_phone_number || "";

        return (
          customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          phone.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      return true;
    });

    return filtered;
  };

  // Pagination
  const movements = filteredMovements();
  const totalPages = Math.ceil(movements.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMovements = movements.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="inventory-container" style={styles.container}>
          <TopActionMenus AddTitle="Customer Register" />

          {/* STATISTICS SECTION */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconWrapper,
                  backgroundColor: "#e3f2fd",
                }}
              >
                <FontAwesomeIcon
                  icon={faUsers}
                  style={{ ...styles.statIcon, color: "#1976d2" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{stats.totalCustomers}</h3>
                <p style={styles.statLabel}>Total Customers</p>
              </div>
            </div>
          </div>

          {/* FILTERS AND SEARCH */}
          <div style={styles.filtersSection}>
            <div style={styles.searchContainer}>
              <FontAwesomeIcon icon={faSearch} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search by customer name, email or phone..."
                style={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div style={styles.itemsPerPageContainer}>
              <label style={styles.itemsPerPageLabel}>Show:</label>
              <select
                style={styles.itemsPerPageSelect}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {/* CUSTOMERS TABLE */}
          <div style={styles.card}>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th>Customer Name</th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Address</th>
                    <th>Created</th>
                    <th>Last Modified</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!customers ? (
                    <tr>
                      <td
                        colSpan="7"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin /> Loading
                        customers...
                      </td>
                    </tr>
                  ) : paginatedMovements.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        {searchTerm
                          ? "No customers match your search criteria"
                          : "No customers found"}
                      </td>
                    </tr>
                  ) : (
                    paginatedMovements.map((movement) => {
                      return (
                        <tr key={movement.id} style={styles.tableRow}>
                          <td>{movement.attributes.title}</td>
                          <td>
                            {movement.attributes.field_phone_number || "N/A"}
                          </td>
                          <td>
                            {movement.attributes.field_customer_email || "N/A"}
                          </td>
                          <td>
                            {movement.attributes.field_customer_address ||
                              "N/A"}
                          </td>
                          <td>
                            {formatDisplayDate(movement.attributes.created)}
                          </td>
                          <td>
                            {formatDisplayDate(movement.attributes.changed)}
                          </td>
                          <td>
                            <div style={styles.actionsContainer}>
                              <button
                                style={styles.editButton}
                                title="Edit Customer"
                                onClick={() => handleEditClick(movement)}
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                style={styles.deleteButton}
                                title="Delete Customer"
                                onClick={() =>
                                  deleteCustomer(movement.id, movement)
                                }
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  style={styles.paginationButton}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                <div style={styles.pageNumbers}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        style={{
                          ...styles.pageButton,
                          ...(currentPage === pageNum
                            ? styles.activePageButton
                            : {}),
                        }}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  style={styles.paginationButton}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>

                <div style={styles.pageInfo}>
                  Page {currentPage} of {totalPages}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Customer Modal */}
      <EditCustomer
        customer={editingCustomer}
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        onSave={handleEditSave}
        mutateCustomers={mutateCustomers}
      />

      <DashboardContentFooter />
    </div>
  );
}

// Styles
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#ecf2fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "34px 32px 4rem 32px",
  },
  header: {
    marginBottom: "30px",
  },
  title: {
    fontSize: "28px",
    color: "#333",
    marginBottom: "5px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  subtitle: {
    color: "#666",
    fontSize: "16px",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "20px",
    marginBottom: "30px",
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "15px",
    transition: "transform 0.2s, box-shadow 0.2s",
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    },
  },
  statIconWrapper: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statIcon: {
    fontSize: "24px",
  },
  statValue: {
    margin: 0,
    fontSize: "24px",
    fontWeight: "700",
    color: "#333",
  },
  statLabel: {
    margin: "5px 0 0 0",
    color: "#666",
    fontSize: "13px",
    fontWeight: "500",
  },
  filtersSection: {
    display: "flex",
    gap: "20px",
    marginBottom: "20px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  searchContainer: {
    flex: 1,
    position: "relative",
    minWidth: "300px",
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#666",
  },
  searchInput: {
    width: "100%",
    padding: "12px 10px 12px 40px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    fontSize: "14px",
    transition: "border-color 0.2s",
    "&:focus": {
      outline: "none",
      borderColor: "#007bff",
    },
  },
  filterContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  filterLabel: {
    color: "#666",
    fontSize: "14px",
  },
  filterSelect: {
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    background: "white",
    fontSize: "14px",
  },
  itemsPerPageContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  itemsPerPageLabel: {
    color: "#666",
    fontSize: "14px",
  },
  itemsPerPageSelect: {
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    background: "white",
    fontSize: "14px",
    cursor: "pointer",
  },
  card: {
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  tableContainer: {
    overflowX: "auto",
    marginTop: "10px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1000px",
    borderRadius: "8px",
  },
  tableRow: {
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "#f8f9fa",
    },
  },
  th: {
    background: "#f8f9fa",
    padding: "14px 12px",
    textAlign: "left",
    borderBottom: "2px solid #e0e0e0",
    color: "#555",
    fontWeight: "600",
    fontSize: "13px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  td: {
    padding: "14px 12px",
    borderBottom: "1px solid #f0f0f0",
    verticalAlign: "top",
    fontSize: "14px",
    color: "#444",
  },
  marginBadge: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "600",
  },
  // Actions Styles
  actionsContainer: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  viewButton: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#bbdefb",
      transform: "scale(1.05)",
    },
  },
  editButton: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#c8e6c9",
      transform: "scale(1.05)",
    },
  },
  deleteButton: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#ffcdd2",
      transform: "scale(1.05)",
    },
  },
  // Delete Modal Styles
  deleteModalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  deleteModal: {
    background: "white",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    minWidth: "400px",
    maxWidth: "500px",
  },
  deleteModalTitle: {
    margin: "0 0 15px 0",
    color: "#333",
    fontSize: "18px",
    fontWeight: "600",
  },
  deleteModalText: {
    margin: "0 0 15px 0",
    color: "#666",
    fontSize: "14px",
  },
  deleteModalDetails: {
    background: "#f8f9fa",
    padding: "15px",
    borderRadius: "8px",
    marginBottom: "20px",
    fontSize: "14px",
  },
  deleteModalActions: {
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
  },
  deleteConfirmButton: {
    background: "#dc3545",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "&:hover": {
      background: "#c82333",
    },
    "&:disabled": {
      background: "#6c757d",
      cursor: "not-allowed",
    },
  },
  deleteCancelButton: {
    background: "#6c757d",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "&:hover": {
      background: "#5a6268",
    },
    "&:disabled": {
      background: "#6c757d",
      cursor: "not-allowed",
    },
  },
  // Message Styles
  successMessage: {
    background: "#d4edda",
    color: "#155724",
    padding: "12px 20px",
    borderRadius: "6px",
    marginBottom: "20px",
    border: "1px solid #c3e6cb",
    display: "flex",
    alignItems: "center",
  },
  errorMessage: {
    background: "#f8d7da",
    color: "#721c24",
    padding: "12px 20px",
    borderRadius: "6px",
    marginBottom: "20px",
    border: "1px solid #f5c6cb",
    display: "flex",
    alignItems: "center",
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px solid #e0e0e0",
  },
  paginationButton: {
    background: "#f8f9fa",
    border: "1px solid #dee2e6",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    "&:hover:not(:disabled)": {
      background: "#e9ecef",
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
  pageNumbers: {
    display: "flex",
    gap: "8px",
  },
  pageButton: {
    background: "white",
    border: "1px solid #dee2e6",
    padding: "8px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    minWidth: "40px",
    transition: "all 0.2s",
    "&:hover": {
      background: "#f8f9fa",
    },
  },
  activePageButton: {
    background: "#007bff",
    color: "white",
    borderColor: "#007bff",
    "&:hover": {
      background: "#0056b3",
    },
  },
  pageInfo: {
    color: "#666",
    fontSize: "14px",
  },
};
